import React, { useEffect, useRef, useState } from "react";

/**
 * useInView — fires `true` once when the element first becomes ≥ threshold visible
 * in the viewport.  Stays `true` afterwards so we don't re-play animations on scroll.
 */
export function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current || visible) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold, rootMargin: "0px 0px -10% 0px" }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold, visible]);
  return [ref, visible];
}

/** Reveal — fades + slides children up when scrolled into view. */
export function Reveal({ children, delay = 0, className = "", as: Tag = "div", ...rest }) {
  const [ref, inView] = useInView(0.15);
  return (
    <Tag
      ref={ref}
      className={`reveal-elem ${inView ? "is-in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * CountUp — animates a numeric value from 0 → `value` once scrolled into view.
 * Accepts prefix/suffix, custom duration (ms), and decimals.
 */
export function CountUp({
  value,
  prefix = "",
  suffix = "",
  duration = 1400,
  decimals = 0,
  className = "",
  testId,
}) {
  const [ref, inView] = useInView(0.3);
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setN(value * eased);
      if (t < 1) raf = requestAnimationFrame(step);
      else setN(value);
    };
    raf = requestAnimationFrame(step);
    // Safety fallback in case rAF is paused (headless tests)
    const fallback = setInterval(() => {
      const t = Math.min(1, (performance.now() - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setN(value * eased);
      if (t >= 1) clearInterval(fallback);
    }, 40);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(fallback);
    };
  }, [inView, value, duration]);

  const formatted = n.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}

/**
 * AnimatedPoly — SVG polyline that "draws itself" left-to-right when in view.
 * Uses pathLength="1" so we don't have to measure the path manually.
 */
export function AnimatedPoly({ points, color, strokeWidth = 2, delay = 0, duration = 1400 }) {
  const [ref, inView] = useInView(0.25);
  return (
    <polyline
      ref={ref}
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      points={points}
      pathLength="1"
      strokeDasharray="1"
      strokeDashoffset={inView ? 0 : 1}
      style={{
        transition: `stroke-dashoffset ${duration}ms cubic-bezier(0.65, 0, 0.35, 1) ${delay}ms`,
      }}
    />
  );
}

/**
 * AnimatedBar — horizontal progress bar that grows from 0 → pct% on view.
 */
export function AnimatedBar({ pct, max = 100, color, delay = 0, height = 6, trackColor }) {
  const [ref, inView] = useInView(0.3);
  const width = inView ? `${Math.min(100, (pct / max) * 100)}%` : "0%";
  return (
    <div
      ref={ref}
      className="rounded-full overflow-hidden"
      style={{ height, background: trackColor || "rgba(148,163,184,0.18)" }}
    >
      <div
        className="h-full rounded-full"
        style={{
          width,
          background: color,
          transition: `width 1.2s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        }}
      />
    </div>
  );
}

/**
 * AnimatedDonut — donut chart using SVG strokes that sweep in on view.
 * `segments` = [{ pct, color, label }]; pct values must sum ≤ 100.
 * Track color is the muted background ring.
 */
export function AnimatedDonut({ segments, size = 110, stroke = 16, trackColor = "rgba(148,163,184,0.22)" }) {
  const [ref, inView] = useInView(0.35);
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const total = segments.reduce((s0, s) => s0 + (Number(s.value ?? s.pct) || 0), 0);
  let offset = 0;

  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ transform: "rotate(-90deg)" }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={trackColor}
        strokeWidth={stroke}
      />
      {segments.map((s, i) => {
        const frac = total > 0 ? (Number(s.value ?? s.pct) || 0) / total : 0;
        const len = frac * C;
        const dashArr = inView ? `${len} ${C - len}` : `0 ${C}`;
        const el = (
          <circle
            key={`${s.color}-${i}`}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={stroke}
            strokeLinecap="butt"
            strokeDasharray={dashArr}
            strokeDashoffset={-offset}
            style={{
              transition: `stroke-dasharray 0.9s cubic-bezier(0.22, 1, 0.36, 1) ${i * 150}ms`,
            }}
          />
        );
        offset += len;
        return el;
      })}
    </svg>
  );
}

/**
 * AnimatedRing — single-arc progress ring used for readiness scores.
 */
export function AnimatedRing({ pct, size = 120, stroke = 12, color = "#006CFF", trackColor = "rgba(148,163,184,0.22)" }) {
  const [ref, inView] = useInView(0.4);
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const len = inView ? (pct / 100) * C : 0;
  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ transform: "rotate(-90deg)" }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={trackColor}
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${len} ${C}`}
        style={{
          transition: "stroke-dasharray 1.4s cubic-bezier(0.65, 0, 0.35, 1) 100ms",
        }}
      />
    </svg>
  );
}
